/**
 * Atom: Spinner
 *
 * Sizes: sm (16) | md (24) | lg (40)
 *
 * Usage:
 *   <Spinner />
 *   <Spinner size="lg" color={tokens.color.brand.gold} />
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg' | number
  color?: string
}

const sizeMap = { sm: 16, md: 24, lg: 40 }

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = tokens.color.brand.gold,
}) => {
  const px = typeof size === 'number' ? size : sizeMap[size]
  const r = px * 0.35
  const dash = r * 2 * Math.PI * 0.75

  return (
    <svg
      width={px}
      height={px}
      viewBox={`0 0 ${px} ${px}`}
      style={{ animation: 'ml-spin 0.8s linear infinite', flexShrink: 0 }}
      role="status"
      aria-label="Loading"
    >
      <circle
        cx={px / 2}
        cy={px / 2}
        r={r}
        fill="none"
        stroke={`${color}33`}
        strokeWidth={px * 0.08}
      />
      <circle
        cx={px / 2}
        cy={px / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={px * 0.08}
        strokeDasharray={`${dash} ${r * 2 * Math.PI}`}
        strokeLinecap="round"
      />
      <style>{`@keyframes ml-spin { to { transform: rotate(360deg); transform-origin: center; } }`}</style>
    </svg>
  )
}

/** Full-page loading overlay */
export const PageSpinner: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: tokens.color.background.page,
  }}>
    <Spinner size="lg" />
  </div>
)
